import { type HTMLAttributes, type ReactElement, useDeferredValue, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';

export const Panel = styled.div`
  width: var(--navbar-width);
  height: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const PanelSection = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  ${legacyOnly(css`
    gap: 0.5em;
  `)}
  ${newDesignOnly(css`
    gap: var(--spacing-xxxs);
  `)}

  overflow-y: auto;
  ${legacyOnly(
    theme => css`
      scrollbar-color: ${theme.sideMenuText} rgba(0, 0, 0, 0);
    `
  )}
  ${newDesignOnly(css`
    scrollbar-color: var(--text-body-on-dark) transparent;
  `)}

  ::-webkit-scrollbar {
    width: 9px;
  }

  ::-webkit-scrollbar-thumb {
    ${legacyOnly(
      theme => css`
        background-color: ${theme.sideMenuText};
      `
    )}
    ${newDesignOnly(css`
      background-color: var(--text-body-on-dark);
    `)}
    border-radius: 10px;
  }
`;

export type ItemProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  noHover?: boolean;
};

export const BaseItemContainer = styled.div`
  justify-content: flex-start;
  display: flex;
  align-items: center;
  ${legacyOnly(css`
    padding: 8px 8px 8px 15px;
    gap: 12px;
  `)}
  ${newDesignOnly(css`
    padding: var(--padding-xs) var(--padding-md);
    gap: var(--spacing-xs);
  `)}
  min-width: 0;
`;

export const Item = styled(BaseItemContainer)<ItemProps>`
  height: 32px;
  cursor: pointer;
  ${legacyOnly(
    theme => css`
      color: ${theme.navbarText};
      font-size: 13px;
    `
  )}
  ${newDesignOnly(css`
    color: var(--text-heading-on-dark);
    font-size: var(--font-size-labels-label-md);
    font-weight: var(--type-weight-label);
    line-height: var(--line-height-labels-label-md);
  `)}
  border-radius: 4px;

  /* Make SVG icons stroke-only with text color */
  ${legacyOnly(
    theme => css`
      svg {
        color: ${theme.navbarText};
        stroke: ${theme.navbarText};
        stroke-width: 0.7px;

        * {
          stroke: currentColor !important;
          stroke-width: inherit;
        }
      }
    `
  )}
  /* The stroke trick above is for the legacy SVG assets; it would thicken antd's outlined glyphs. */
  ${newDesignOnly(css`
    svg {
      font-size: 16px;
      color: var(--text-heading-on-dark);
    }
  `)}

  /*
   * One guarded block, not the branch helpers: these styles also depend on the active and noHover
   * props, which the helpers cannot take.
   *
   * The marker is a ::before bar, not a left border. A border declared here never reaches the element:
   * the reset zeroes its width and the computed style comes back 0px none, while the radius and colour
   * from this same block apply. Border Width has no 3px step, hence the literal.
   */
  ${({ active, noHover, theme }) =>
    theme.isNewDesign
      ? css`
          ${
            active &&
            css`
              background-color: var(--surface-navigation-navigation-item);
              border-radius: 0;
              color: var(--color-neutral-300);
              position: relative;

              &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 3px;
                height: 100%;
                background-color: var(--surface-primary-action);
              }
            `
          }

          ${
            !noHover &&
            css`
              &:hover {
                background-color: var(--surface-navigation-navigation-item);
              }
            `
          }
        `
      : css`
          ${
            active &&
            `
      background-color: ${theme.tertiaryGray};
      font-weight: bold;
      border-left: 3px solid ${theme.limeYellow};
      border-radius: 0;
      padding-left: 12px;
    `
          }

          ${
            !noHover &&
            `
      &:hover {
        background-color: ${theme.tertiaryGray};
        font-weigh: 500;
      }
    `
          }
        `}
`;

type ParentHeightProps = {
  bypass?: boolean;
  fallbackHeight?: number;
  children: (availableHeight?: number) => ReactElement;
};

export const ParentHeight = ({ bypass, fallbackHeight, children }: ParentHeightProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(fallbackHeight);
  const deferredHeight = useDeferredValue(availableHeight);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement;
    if (parent && !bypass) {
      const handleResize = () => setAvailableHeight(parent.clientHeight || fallbackHeight);
      handleResize();
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(parent);
      return () => resizeObserver.disconnect();
    }
  }, [bypass, fallbackHeight]);

  return bypass ? children() : <ParentHeightWrapper ref={ref}>{children(deferredHeight)}</ParentHeightWrapper>;
};

const ParentHeightWrapper = styled.div`
  max-height: 100%;
  overflow: hidden;
`;

export const ItemText = styled.span`
  ${legacyOnly(css`
    font-size: 14px;
  `)}
  ${newDesignOnly(css`
    font-size: var(--font-size-labels-label-md);
  `)}
  vertical-align: middle;
  display: inline-block;
`;
